import React, { useState } from "react";
import { Bot, X, Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * LiveWorldAinaSheet — the natural-language parser bottom-sheet.
 *
 * User types "200 kg iron under ₹50 near me" → we call
 * POST /api/live-world/aina/parse which returns structured filters +
 * a first page of matching results. The sheet then calls `onApply(parsed)`
 * so the parent can update its filter state.
 *
 * Scope guardrails (enforced backend-side too):
 *  • Filters ONLY. No order creation, no negotiation, no financial commit.
 *  • Radius / price bounds are clipped to sane defaults.
 */
const EXAMPLES = [
  "200 kg iron under ₹50 near me",
  "2BHK flat under 40k in Bandra",
  "auction closing today under ₹1L",
  "silver coins under ₹10,000",
  "gold rate right now",
];

export default function LiveWorldAinaSheet({ open, onClose, loc, currency = "INR", onApply }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);

  const run = async (q) => {
    const query = (q ?? text).trim();
    if (!query) return;
    setBusy(true); setText(query);
    try {
      const r = await api.post("/live-world/aina/parse", {
        text:              query,
        lat:               loc?.lat,
        lng:               loc?.lng,
        currency,
        previous_filters:  parsed?.filters || undefined,   // P3.2 · conversational refinement
      });
      setParsed(r.data);
      if (r.data?.parsed_ok !== false) {
        setText("");   // ready for the next refinement
      }
      if (r.data?.parsed_ok === false) {
        toast.error(r.data?.error
          ? `AINA couldn't parse — ${r.data.error}. Try rephrasing.`
          : "AINA couldn't understand that query. Try rephrasing.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AINA request failed. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!parsed?.filters) return;
    if (parsed.parsed_ok === false) {
      toast.error("Nothing to apply — AINA couldn't parse this query.");
      return;
    }
    onApply?.(parsed);
    onClose?.();
  };

  const reset = () => { setParsed(null); setText(""); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60" onClick={onClose} data-testid="lw-aina-sheet">
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 space-y-3 max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto"/>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#E05E36] to-[#c94e2a] grid place-items-center text-white shadow">
            <Bot size={18}/>
          </div>
          <div>
            <div className="font-black text-stone-900 text-lg leading-tight">AINA in Live World</div>
            <div className="text-[11px] text-stone-500 font-bold">Filter only. AINA doesn't bid, buy or negotiate.</div>
          </div>
          <button className="ml-auto p-2 rounded-full hover:bg-stone-100" onClick={onClose}><X size={18}/></button>
        </div>

        {/* Input */}
        <div className="bg-stone-50 rounded-2xl p-3 flex items-center gap-2">
          <Sparkles size={16} className="text-[#E05E36]"/>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder={parsed?.parsed_ok
              ? "Refine: 'only within 20 km', 'show WFC', etc."
              : "e.g. 200 kg iron under ₹50 near me"}
            className="flex-1 bg-transparent outline-none text-sm font-medium text-stone-900"
            data-testid="lw-aina-input"
            autoFocus
          />
          <button
            onClick={() => run()}
            disabled={busy || !text.trim()}
            className="p-2 rounded-full bg-[#E05E36] text-white disabled:opacity-40"
            data-testid="lw-aina-submit"
          >
            {busy ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
          </button>
        </div>

        {/* Examples */}
        {!parsed && !busy && (
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Try one of these</div>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex, i) => (
                <button key={i}
                        onClick={() => run(ex)}
                        className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-800 text-xs font-bold hover:bg-stone-200"
                        data-testid={`lw-aina-example-${i}`}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Parsed filters + preview */}
        {parsed && (
          <div className="space-y-3">
            {parsed.parsed_ok === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-amber-900 text-xs" data-testid="lw-aina-failure">
                <div className="font-black text-sm mb-0.5">Couldn't understand that</div>
                <div className="text-[11px] opacity-90">
                  AINA couldn't parse your query{parsed.error ? ` (${parsed.error})` : ""}. Try rephrasing —
                  add a product, price and location. Example: "10 g gold jewellery under ₹50,000 in Mumbai".
                </div>
              </div>
            )}
            <div className="bg-stone-50 rounded-2xl p-3 space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">
                {parsed.parsed_ok === false ? "Fallback defaults" : "Understood filters"}
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {parsed.filters.product && <Chip label={`Product: ${parsed.filters.product}`}/>}
                {parsed.filters.kind && <Chip label={`Kind: ${parsed.filters.kind}`}/>}
                {parsed.filters.category && <Chip label={`Category: ${parsed.filters.category}`}/>}
                {parsed.filters.quantity && <Chip label={`Qty: ${parsed.filters.quantity.value} ${parsed.filters.quantity.unit}`}/>}
                {parsed.filters.price_max != null && <Chip label={`≤ ₹${parsed.filters.price_max}`} tone="green"/>}
                {parsed.filters.price_min != null && <Chip label={`≥ ₹${parsed.filters.price_min}`} tone="green"/>}
                {parsed.filters.radius_km && <Chip label={`${parsed.filters.radius_km} km radius`}/>}
              </div>
              <div className="text-[10px] text-stone-400 pt-1">
                Confidence: {Math.round((parsed.filters.confidence || 0) * 100)}%
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">
              {parsed.total} matching result{parsed.total !== 1 ? "s" : ""}
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {(parsed.results || []).slice(0, 6).map((r) => (
                <div key={r.id} className="bg-white border border-stone-200 rounded-xl p-2">
                  <div className="text-[10px] uppercase font-black text-stone-500">{r.kind}</div>
                  <div className="text-xs font-black text-stone-900 truncate">{r.title}</div>
                  <div className="text-[11px] font-bold text-emerald-700">
                    {r.ask != null && `₹${Number(r.ask).toLocaleString("en-IN")}`}
                    {r.ask == null && r.bid != null && `₹${Number(r.bid).toLocaleString("en-IN")}`}
                    {r.ask == null && r.bid == null && r.price != null && `₹${Number(r.price).toLocaleString("en-IN")}`}
                  </div>
                </div>
              ))}
              {parsed.results?.length === 0 && (
                <div className="col-span-2 text-center text-xs text-stone-400 py-4">
                  No results match those filters. Try widening your budget or radius.
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button onClick={reset} className="flex-1 py-2.5 rounded-full bg-stone-100 text-stone-800 font-black text-sm">
                Reset
              </button>
              <button onClick={apply}
                      disabled={parsed.parsed_ok === false}
                      className="flex-1 py-2.5 rounded-full bg-emerald-600 text-white font-black text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="lw-aina-apply">
                Apply filters
              </button>
            </div>
            <div className="text-[10px] text-stone-400 text-center italic">
              {parsed.notice}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, tone = "stone" }) {
  const map = {
    stone: "bg-white text-stone-700 border-stone-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-bold border ${map[tone] || map.stone}`}>{label}</span>;
}
